import { Icon } from "@iconify/react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Typography,
  Modal,
  Select, // Import Select component
} from "antd";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { ContentWrapper } from "~/Components";
import styles from "../../Contract.module.scss";
import { ErrorMessage, Field, FieldProps, Formik } from "formik";
import { useAuth } from "~/Hooks/useAuth";
import { useNotification } from "~/Hooks/useNotification";
import { get, post, put } from "@services/api.service";
import { SuccessModal } from "@components/SuccessModal/SuccessModal";
import { useQuery } from "~/Hooks/useQuery";
import { sortBy, uniqueId } from "lodash";
import { subsidiaryList } from "~/Utils";

interface FormItems {
  subsidiaryName: string;
  subsidiaryAdmins: {
    name: string;
    email: string;
  }[];
  recipientLock: boolean;
}

const defaultValue = {
  subsidiaryName: "",
  subsidiaryAdmins: [
    {
      name: "",
      email: "",
    },
  ],
  recipientLock: false,
} as FormItems;

export const AddNewSubsidiary = () => {
  const { user } = useAuth();
  const { openToast, openNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [initialValue, setInitialValue] = useState<FormItems>(defaultValue);
  const [subsidiaryDetails, setSubsidiaryDetails] = useState<any>({});
  const [openModal, setOpenModal] = useState(false);
  const [subsidiaryValues, setSubisidiaryValues] = useState<FormItems>();
  const [successOpen, setSuccessOpen] = useState(false);
  const { id } = useParams();
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const navigate = useNavigate();
  const query = useQuery();
  const subsidiaryId = query?.get("subsidiaryId") || "";
  const viewItem = query?.get("view") || "";
  useEffect(() => {
    if (subsidiaryId) {
      get(`v1/subsidiary/subsidiary-detail?subsidiaryId=${subsidiaryId}`)
        .then((res: any) => {
          if (res && res.response && res.response.data) {
            setSubsidiaryDetails(res.response.data);
            let admins = res.response.data.subsidiaryAdmins;
            const subsidiaryAdminData =
              admins &&
              admins.map((item: any) => ({
                ...item,
                name: item.name,
                email: item.email,
              }));
            const values = {
              subsidiaryName: res.response.data.subsidiaryName,
              subsidiaryAdmins: subsidiaryAdminData || [],
              subsidiaryId: res.response.data.subsidiaryId,
            } as unknown as FormItems;
            setInitialValue(values);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      get(`v1/spa/subsidiary/update-subsidiary?templateId=${id}`)
        .then((res: any) => {
          res = res.response.data;
          const values = {
            subsidiaryName: res.data.subsidiaryName,
            subsidiaryAdmins: [
              {
                name: res.data.name,
                email: res.data.email,
              },
            ],
          } as FormItems;
          setInitialValue(values);
          setIsLoading(false);
        })
        .catch(() => {
          openToast({
            content: "Getting data failed",
            type: "error",
          });
          setIsLoading(false);
        });
    }
  }, [id, openToast]);

  const onSubmit = (values: FormItems) => {
    setOpenModal(true);
    setSubisidiaryValues(values);
  };

  const [approverData, setApproverData] = useState([]);
  const [approverDataByEmail, setApproverDataByEmail] = useState<any>([]);


  const getUserRecords = () => {
    const subsidiaryUrl = `v1/user/get-admin-user-list`;
    get(subsidiaryUrl)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          const responseData = res.response.data;
          if (responseData) {
            setApproverData(responseData);
            setApproverDataByEmail(sortBy(responseData, ['email']));
          } else {
            console.error("No records found in the response");
            setApproverData([]);
          }
        } else {
          console.error("Unexpected response structure:", res);
        }
      })
      .catch((err) => {
        console.error("API Error:", err);
        openToast({
          content: "Getting Approvers failed",
          type: "error",
        });
      });
  };

  useEffect(() => {
    getUserRecords();
  }, []);

  const onHandleSubmit = async () => {
    if (!subsidiaryValues) {
      return;
    }

    const { recipientLock, ...payload } = subsidiaryValues;
    const finalPayload = {
      ...payload,
      subsidiaryId: subsidiaryId,
      versionOrder: subsidiaryDetails.versionOrder,
    };
    setIsSubmitLoading(true);
    if (subsidiaryId) {
      put(`v1/subsidiary/update-subsidiary`, finalPayload)
        .then((res: any) => {
          if (res) {
            setSuccessOpen(true);
          }
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          openNotification({
            title: err,
            message: "Subsidiary/Division updating failed",
            type: "error",
          });
          setIsSubmitLoading(false);
        });
    } else {
      post(`v1/subsidiary/create-subsidiary`, payload)
        .then((res: any) => {
          setSuccessOpen(true);
          setIsSubmitLoading(false);
        })
        .catch((err) => {
          // openNotification({
          //   title: err,
          //   message: "Subsidiary/Division Creation failed",
          //   type: "error",
          // });
          setIsSubmitLoading(false);
        });
    }
  };

  const onCancel = () => {
    setOpenModal(false);
    setIsSubmitLoading(false);
  };

  const validationSchema1 = yup.object().shape({
    subsidiaryName: yup
      .string()
      .trim()
      .required("Subsidiary/Division Name is required")
      .matches(/^\S(?:.*\S)?$/, "Subsidiary/Division Name cannot start or end with whitespace"),
    subsidiaryAdmins: yup.array().of(
      yup.object().shape({
        name: yup
          .string()
          .trim()
          .required("User Name is required")
          .matches(/^\S(?:.*\S)?$/, "User Name cannot start or end with whitespace"),
        email: yup.string().trim().email("Invalid email address").required("User Email is required"),
      })
    ),
  });

  return (
    <>
      <ContentWrapper
        loading={isLoading}
        customTitle={
          <Space align="start">
            <Icon
              onClick={() => navigate(-1)}
              icon="akar-icons:arrow-left"
              style={{
                fontSize: "1.8rem",
                cursor: "pointer",
                color: styles.primary,
                marginRight: styles.whitespace1,
              }}
            />
            <span>
              <Typography.Title
                level={3}
                style={{ margin: 0 }}
              >
                {viewItem ? "View" : subsidiaryId ? "Edit" : "Create"} Subsidiary/Division
              </Typography.Title>
            </span>
          </Space>
        }
      >
        <Formik
          initialValues={initialValue}
          onSubmit={onSubmit}
          validationSchema={validationSchema1}
        >
          {(props) => {
            const { setFieldValue, setFieldTouched } = props;

            useEffect(() => {
              initialValue.subsidiaryAdmins.map((subsidiary: any, index: any) => {
                if (subsidiary) {
                  setFieldValue(`subsidiaryAdmins[${index}].email`, subsidiary.email);
                  setFieldValue(`subsidiaryAdmins[${index}].name`, subsidiary.name);
                }
              });
              setFieldValue(`subsidiaryName`, initialValue.subsidiaryName);
            }, [initialValue]);
            useEffect(() => {
              let index = initialValue.subsidiaryAdmins.length - 1;
              setTimeout(() => {
                let index = initialValue.subsidiaryAdmins.length - 1;
                setFieldTouched(`subsidiaryAdmins[${index}].email`, false);
                setFieldTouched(`subsidiaryAdmins[${index}].name`, false);
                setFieldTouched(`subsidiaryName`, false);
              }, 0);
            }, [subsidiaryId, initialValue, props.values]);

            const adminsMail = props.values.subsidiaryAdmins.map((val: any) => {
              return val.email;
            });

            const adminsName = props.values.subsidiaryAdmins.map((val: any) => {
              return val.name;
            });

            return (
              <>
                <Form
                  layout="vertical"
                  onFinish={props.handleSubmit}
                >
                  <Row style={{ marginTop: 16 }}>
                    <Col
                      xs={24}
                      md={16}
                    >
                      <div id="step1">
                        <Row>
                          <Col span={12}>
                            <div style={{ textAlign: "left" }}>
                              <Typography.Title
                                level={4}
                                style={{ marginBottom: styles.whitespace3 }}
                              >
                                Subsidiary/Division Details
                              </Typography.Title>
                            </div>
                          </Col>
                        </Row>
                        <div>
                          <Row
                            gutter={[16, 16]}
                            align="top"
                          >
                            <Col span={8}>
                              <Form.Item label="Subsidiary/Division">
                                <Field name="subsidiaryName">
                                  {({ field }: FieldProps) => (
                                    <Input
                                      // disabled={viewItem ? true : false}
                                      {...field}
                                      style={{
                                        cursor: props.values.recipientLock ? "not-allowed" : "auto",
                                      }}
                                      autoComplete="off"
                                      readOnly={viewItem ? true : false}
                                    />
                                  )}
                                </Field>
                                <Typography.Text type="danger">
                                  <ErrorMessage name="subsidiaryName" />
                                </Typography.Text>
                              </Form.Item>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={12}>
                              <div style={{ textAlign: "left" }}>
                                <Typography.Title
                                  level={4}
                                  style={{ marginBottom: styles.whitespace3 }}
                                >
                                  Subsidiary/Division Admins
                                </Typography.Title>
                              </div>
                            </Col>
                          </Row>
                          {props.values.subsidiaryAdmins.map((_, i) => (
                            <Row
                              gutter={[16, 16]}
                              align="top"
                              key={`user${i}`}
                            >
                              <Col span={8}>
                                <Form.Item label="Name">
                                  <Field name={`subsidiaryAdmins[${i}].name`}>
                                    {({ field, form }: FieldProps) => (
                                      <Select
                                        className={viewItem ? "viewItem" : "none"}
                                        disabled={viewItem ? true : false}
                                        showSearch
                                        {...field}
                                        style={{ width: "100%" }}
                                        placeholder="Select a name"
                                        onChange={(value, option: any) => {
                                          const selectedEmail = option ? option.email : "";
                                          form.setFieldValue(`subsidiaryAdmins[${i}].name`, value);
                                          form.setFieldValue(`subsidiaryAdmins[${i}].email`, selectedEmail);

                                          const updatedSelectedNames = value
                                            ? [...selectedNames, value]
                                            : selectedNames.filter((name) => name !== field.value);
                                          setSelectedNames(updatedSelectedNames);
                                        }}
                                        filterOption={(input, option: any) =>
                                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                      >
                                        {approverData
                                          .filter((user: any) => !adminsName.includes(user.name))
                                          .map((user: any, index: any) => (
                                            <Select.Option
                                              key={index}
                                              value={user.name}
                                              email={user.email}
                                            >
                                              {user.name}
                                            </Select.Option>
                                          ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`subsidiaryAdmins[${i}].name`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              <Col span={8}>
                                <Form.Item label="Email">
                                  <Field name={`subsidiaryAdmins[${i}].email`}>
                                    {({ field, form }: FieldProps) => (
                                      <Select
                                        className={viewItem ? "viewItem" : "none"}
                                        disabled={viewItem ? true : false}
                                        showSearch
                                        {...field}
                                        style={{ width: "100%" }}
                                        placeholder="Select an email"
                                        onChange={(value, option: any) => {
                                          const selectedName = option ? option.label : "";
                                          form.setFieldValue(`subsidiaryAdmins[${i}].email`, value);
                                          form.setFieldValue(`subsidiaryAdmins[${i}].name`, selectedName);

                                          const updatedSelectedEmails = value
                                            ? [...selectedEmails, value]
                                            : selectedEmails.filter((email) => email !== field.value);
                                          setSelectedEmails(updatedSelectedEmails);
                                        }}
                                        filterOption={(input, option: any) =>
                                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                      >
                                        {approverDataByEmail
                                          .filter((email: any) => !adminsMail.includes(email.email))
                                          .map((email: any, index: any) => (
                                            <Select.Option
                                              key={index}
                                              value={email.email}
                                              label={email.name}
                                            >
                                              {email.email}
                                            </Select.Option>
                                          ))}
                                      </Select>
                                    )}
                                  </Field>
                                  <Typography.Text type="danger">
                                    <ErrorMessage name={`subsidiaryAdmins[${i}].email`} />
                                  </Typography.Text>
                                </Form.Item>
                              </Col>
                              {!viewItem && (
                                <Col span={2}>
                                  <Form.Item label=" ">
                                    <Button
                                      type="link"
                                      danger
                                      disabled={props.values.subsidiaryAdmins.length === 1}
                                      onClick={() => {
                                        const subsidiaryAdmins = [...props.values.subsidiaryAdmins];
                                        subsidiaryAdmins.splice(i, 1);
                                        props.setValues({
                                          ...props.values,
                                          subsidiaryAdmins: subsidiaryAdmins ? subsidiaryAdmins : [],
                                        });
                                      }}
                                    >
                                      {" "}
                                      <Icon
                                        icon="fluent:delete-16-regular"
                                        style={{
                                          color: styles.strawberry,
                                          cursor: "pointer",
                                          fontSize: 20,
                                        }}
                                      />
                                    </Button>
                                  </Form.Item>
                                </Col>
                              )}
                            </Row>
                          ))}
                          {!viewItem && (
                            <Row gutter={16}>
                              <Col
                                span={24}
                                style={{ textAlign: "right", marginBottom: 16 }}
                              >
                                <Button
                                  type="text"
                                  icon={<Icon icon="akar-icons:plus" />}
                                  style={{
                                    cursor: props.values.recipientLock ? "not-allowed" : "auto",
                                  }}
                                  disabled={
                                    props.values.recipientLock ||
                                    props.values.subsidiaryAdmins.some((val) => val.name === "" || val.email === "")
                                  }
                                  onClick={() =>
                                    props.setValues({
                                      ...props.values,
                                      subsidiaryAdmins: [
                                        ...props.values.subsidiaryAdmins,
                                        {
                                          name: "",
                                          email: "",
                                        },
                                      ],
                                    })
                                  }
                                >
                                  Add Another
                                </Button>
                              </Col>
                            </Row>
                          )}
                        </div>
                      </div>
                      {!viewItem && (
                        <Row>
                          <Col
                            xs={24}
                            md={24}
                            style={{ textAlign: "right" }}
                          >
                            <Button
                              htmlType="submit"
                              type="primary"
                              disabled={!props.isValid}
                            >
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                </Form>
              </>
            );
          }}
        </Formik>
        <SuccessModal
          title={`You have successfully ${
            subsidiaryId ? "edited subsidiary/division" : "created a new subsidiary/division"
          } `}
          type={"success"}
          buttonText={"Go Back"}
          redirect={`/subsidiaries`}
          open={successOpen}
          setOpen={setSuccessOpen}
          message={""}
        />
      </ContentWrapper>
      <Modal
        open={openModal}
        onCancel={onCancel}
        width={400}
        style={{ maxHeight: 300 }}
        centered
        footer={[
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Button
              onClick={onCancel}
              style={{ marginRight: "10px" }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onHandleSubmit}
              loading={isSubmitLoading}
            >
              Submit
            </Button>
          </div>,
        ]}
      >
        <div>
          {subsidiaryId ? (
            <p>Are you sure to edit this Subsidiary/Division?</p>
          ) : (
            <p>Are you sure to add new Subsidiary/Division?</p>
          )}
        </div>
      </Modal>
    </>
  );
};
